import React from 'react';
import styles from './index.module.scss';

export type HomePageSkeletonSectionType =
  | 'zoneBanner'
  | 'recommend'
  | 'categories'
  | 'specialCards'
  | 'themeZone'
  | 'featured';

interface HomePageSkeletonSectionProps {
  type: HomePageSkeletonSectionType;
}

const SECTION_ORDER: HomePageSkeletonSectionType[] = [
  'zoneBanner',
  'recommend',
  'categories',
  'specialCards',
  'themeZone',
  'featured',
];

const ZoneBannerSection = () => (
  <section className={styles.zoneBannerSection} aria-hidden="true">
    <div className={styles.zoneBannerRow}>
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={`zone-${index}`} className={styles.zoneBannerItem} />
      ))}
    </div>
  </section>
);

const RecommendSection = () => (
  <section className={styles.recommendSection} aria-hidden="true">
    <div className={styles.sectionHeader}>
      <div className={styles.sectionTitleBone} />
      <div className={styles.sectionMoreBone} />
    </div>
    <div className={styles.recommendRow}>
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={`recommend-${index}`} className={styles.recommendCard}>
          <div className={styles.recommendImageBone} />
          <div className={styles.recommendTitleBone} />
          <div className={styles.recommendTitleBoneShort} />
          <div className={styles.recommendPriceBone} />
        </div>
      ))}
    </div>
  </section>
);

const CategoriesSection = () => (
  <section className={styles.categorySection} aria-hidden="true">
    <div className={styles.categoryInner}>
      <div className={styles.sectionTitleBone} />
      <div className={styles.categoryRow}>
        {Array.from({ length: 10 }).map((_, index) => (
          <div key={`category-${index}`} className={styles.categoryItem}>
            <div className={styles.categoryImageBone} />
            <div className={styles.categoryNameBone} />
          </div>
        ))}
      </div>
    </div>
  </section>
);

const SpecialCardsSection = () => (
  <section className={styles.specialCardsSection} aria-hidden="true">
    <div className={styles.specialCardsInner}>
      <div className={styles.sectionTitleBone} />
      <div className={styles.specialCardsRow}>
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={`card-${index}`} className={styles.specialCardBone} />
        ))}
      </div>
    </div>
  </section>
);

const ThemeZoneSection = () => (
  <section className={styles.themeZoneSection} aria-hidden="true">
    <div className={styles.sectionTitleBone} />
    <div className={styles.themeZoneBody}>
      <div className={styles.themeZoneLeftBone} />
      <div className={styles.themeZoneRight}>
        <div className={styles.themeZoneTabs}>
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={`tab-${index}`} className={styles.themeZoneTabBone} />
          ))}
        </div>
        <div className={styles.themeZoneProducts}>
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={`theme-product-${index}`} className={styles.themeZoneProductCard}>
              <div className={styles.themeZoneProductImageBone} />
              <div className={styles.themeZoneProductTitleBone} />
              <div className={styles.themeZoneProductPriceBone} />
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const FeaturedSection = () => (
  <section className={styles.featuredSection} aria-hidden="true">
    <div className={styles.featuredInner}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitleBone} />
        <div className={styles.sectionMoreBone} />
      </div>
      <div className={styles.featuredGrid}>
        {Array.from({ length: 9 }).map((_, index) => (
          <div key={`featured-${index}`} className={styles.featuredItem}>
            <div className={styles.featuredImageBone} />
            <div className={styles.featuredNameBone} />
          </div>
        ))}
      </div>
    </div>
  </section>
);

const SECTION_MAP: Record<HomePageSkeletonSectionType, React.FC> = {
  zoneBanner: ZoneBannerSection,
  recommend: RecommendSection,
  categories: CategoriesSection,
  specialCards: SpecialCardsSection,
  themeZone: ThemeZoneSection,
  featured: FeaturedSection,
};

export const HomePageSkeletonSection: React.FC<HomePageSkeletonSectionProps> = ({ type }) => {
  const Section = SECTION_MAP[type];
  return <Section />;
};

interface HomePageSkeletonProps {
  /** 顶部已由 BigBannerShell 占位时，不再重复渲染 Banner 骨架 */
  hideBanner?: boolean;
}

const HomePageSkeleton: React.FC<HomePageSkeletonProps> = ({ hideBanner = false }) => {
  return (
    <div className={styles.skeletonRoot}>
      {!hideBanner && (
        <section className={styles.bannerSection} aria-hidden="true">
          <div className={styles.bannerBone} />
        </section>
      )}
      {SECTION_ORDER.map(type => (
        <HomePageSkeletonSection key={type} type={type} />
      ))}
    </div>
  );
};

export default HomePageSkeleton;
